import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Scissors, Loader2, X, Image as ImageIcon, Sparkles as SparkIcon, Wand2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { hexToRgb } from "../lib/canvasUtils";

/**
 * GreenScreenStudio — one-stop green-screen control.
 *
 * This is a deliberate rewrite of the fragmented `useGreenScreen` +
 * `GreenScreenToggle` + `StudioFxModal` split. Those three files each
 * owned a slice of the pipeline, passed state across refs, and raced
 * on `replaceTrack`. Every bug the user has hit was rooted in that
 * cross-file plumbing.
 *
 * Design decisions (iter-182):
 *
 *   • ONE component owns: UI, settings, localStorage persistence,
 *     canvas pipeline, outbound-track publication.
 *   • NO hook abstraction. The compositor lives inline in this file so
 *     there are no "which hook is active" questions.
 *   • INSTANT apply. Every click / slider writes settings AND
 *     rebuilds the compositor on the next frame. No Apply button that
 *     can be missed.
 *   • Backgrounds fetched lazily from the existing admin library
 *     endpoints — no backend changes.
 *   • Settings persist to `tapin_host_greenscreen` (the same key the
 *     old path used) so the user's historical config keeps working.
 *
 * Props:
 *   cameraStream       — host's camera MediaStream. When null, the
 *                        component still renders the UI but the
 *                        canvas publishes nothing.
 *   onCompositedTrack  — called with the produced track each time the
 *                        pipeline rebuilds. Called with `null` when
 *                        green screen is disabled (parent should
 *                        restore the raw camera track).
 *   collapsed          — initial UI state (default: true).
 */
const STORAGE_KEY = "tapin_host_greenscreen";
const DEFAULTS = {
  enabled: false,
  bgMode: "none", // none | blur | image | video
  bgUrl: "",
  bgDataUrl: "",
  chromaColor: "#00FF00",
  similarity: 0.55,
  // Default smoothness ≈ 0.15 → ~9 px feather in person-cutout mode,
  // which is the Zoom-style sweet spot. Lower for crisp green-screen
  // chroma key, higher for super-soft alpha.
  smoothness: 0.15,
  // NEW: removal engine
  //   "chroma" → classic chroma-key using chromaColor + similarity
  //              (requires a physical green/blue wall).
  //   "person" → ML-based person segmentation (MediaPipe Selfie
  //              Segmentation). Removes the background regardless of
  //              color — works without any green wall, like Zoom /
  //              Meet / Teams virtual background. Model is lazy-
  //              loaded from a CDN the first time the user switches
  //              to this mode so the initial bundle stays lean.
  removalMode: "chroma",
};

function readSettings() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return { ...DEFAULTS, ...(raw || {}) };
  } catch {
    return { ...DEFAULTS };
  }
}

function writeSettings(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota */ }
}

export default function GreenScreenStudio({
  cameraStream,
  onCompositedTrack,
  collapsed: initialCollapsed = true,
  testidPrefix = "gs",
}) {
  const [settings, setSettings] = useState(readSettings);
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [library, setLibrary] = useState(null); // null = not fetched
  const [showSliders, setShowSliders] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  // Live diagnostic: "idle" | "loading" | "loaded" | "error" | "none"
  // Surfaced in the panel so creators (and us) can see at a glance
  // whether the chosen background actually decoded into the
  // compositor — the single most common "it looks black" bug.
  const [bgStatus, setBgStatus] = useState("idle");
  // Eyedropper mode: when true, the next click on the live preview
  // canvas samples that pixel's color and stores it as chromaColor.
  // This is THE critical UX for real-world webcam green screens, where
  // the captured "green" is never pure RGB(0,255,0) — it's tinted by
  // lighting, fabric, and the camera's color profile. Without an
  // eyedropper, users with real green walls see no keying at all
  // because the default key color never matches what the camera sees.
  const [eyedropper, setEyedropper] = useState(false);
  const [keyCoverage, setKeyCoverage] = useState(null); // % of pixels keyed, 0-100
  // Preview canvas mounted in the panel body — we mirror every
  // composited frame into it so the creator gets immediate visual
  // proof the background is blending correctly, even before the
  // outbound MediaStreamTrack reaches their own preview element.
  const previewCanvasRef = useRef(null);
  // Main composite canvas ref — exposed so the eyedropper handler
  // can read a pixel directly from the live un-keyed camera feed
  // and sample the user's actual green color.
  const rawCameraCanvasRef = useRef(null);
  const eyedropperRef = useRef(false);
  useEffect(() => { eyedropperRef.current = eyedropper; }, [eyedropper]);

  // (F) Manual shutter speed (camera exposure time) control.
  //   - Available on cameras whose driver exposes the `exposureMode`
  //     and `exposureTime` MediaTrackCapabilities. Most desktop
  //     webcams DON'T expose this; most modern Android phones DO,
  //     and the iPhone Pro back camera does.
  //   - Lower exposureTime = faster shutter = less motion blur on the
  //     subject's edges = a sharper segmentation mask. The trade-off
  //     is darker frames; combine with bumping ISO/gain (we don't
  //     expose that) or with extra light on set.
  //   - We surface the slider only when the constraint is supported,
  //     so users on cams that can't do it don't see a useless slider.
  const [shutterCaps, setShutterCaps] = useState(null); // { min, max, step } | null
  const [shutterValue, setShutterValue] = useState(null);
  useEffect(() => {
    const t = cameraStream?.getVideoTracks?.()[0];
    if (!t || !t.getCapabilities) { setShutterCaps(null); return; }
    try {
      const caps = t.getCapabilities();
      if (caps.exposureTime && caps.exposureMode?.includes?.("manual")) {
        setShutterCaps({
          min: caps.exposureTime.min || 1,
          max: caps.exposureTime.max || 10000,
          step: caps.exposureTime.step || 1,
        });
        const settings = t.getSettings?.() || {};
        setShutterValue(settings.exposureTime || caps.exposureTime.min || 1);
      } else {
        setShutterCaps(null);
      }
    } catch { setShutterCaps(null); }
  }, [cameraStream]);
  const applyShutter = async (v) => {
    setShutterValue(v);
    const t = cameraStream?.getVideoTracks?.()[0];
    if (!t) return;
    try {
      await t.applyConstraints({
        advanced: [{ exposureMode: "manual", exposureTime: v }],
      });
    } catch (e) {
      console.warn("[GS] shutter applyConstraints failed:", e);
    }
  };
  const resetShutter = async () => {
    const t = cameraStream?.getVideoTracks?.()[0];
    if (!t) return;
    try {
      await t.applyConstraints({ advanced: [{ exposureMode: "continuous" }] });
      const settings = t.getSettings?.() || {};
      setShutterValue(settings.exposureTime || shutterCaps?.min || 1);
    } catch { /* ignore */ }
  };

  // Persist settings on every change.
  useEffect(() => { writeSettings(settings); }, [settings]);

  // Fetch admin library lazily when the panel first expands. Keeps
  // initial page load snappy.
  useEffect(() => {
    if (collapsed || library !== null) return;
    let cancelled = false;
    api.get("/greenscreen/backgrounds")
      .then(({ data }) => { if (!cancelled) setLibrary(data?.items || []); })
      .catch(() => { if (!cancelled) setLibrary([]); });
    return () => { cancelled = true; };
  }, [collapsed, library]);

  // --- Compositor pipeline (inline, no hooks) ------------------------
  const onTrackRef = useRef(onCompositedTrack);
  useEffect(() => { onTrackRef.current = onCompositedTrack; }, [onCompositedTrack]);
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  useEffect(() => {
    const enabled = settings.enabled;
    const videoTrack = enabled ? cameraStream?.getVideoTracks?.()[0] : null;
    if (!enabled || !videoTrack) {
      // Restore raw camera on the outbound.
      try { onTrackRef.current?.(null); } catch {}
      return undefined;
    }

    // ---- Build pipeline ------------------------------------------
    const W = 1280, H = 720;
    const hidden = document.createElement("div");
    hidden.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;";
    document.body.appendChild(hidden);

    const vid = document.createElement("video");
    vid.muted = true; vid.playsInline = true; vid.autoplay = true;
    vid.srcObject = new MediaStream([videoTrack]);
    hidden.appendChild(vid);
    vid.play().catch(() => {});

    // NOTE: do NOT set crossOrigin up front. We set it per-load based
    // on whether the source is a data-URL (no CORS needed, and some
    // WebKit builds refuse to decode data-URL images when
    // crossOrigin="anonymous" is pre-set) or an http(s) URL (CORS is
    // required to avoid canvas-taint on captureStream).
    const bgImg = new Image();
    const bgVid = document.createElement("video");
    bgVid.muted = true; bgVid.loop = true;
    bgVid.playsInline = true; bgVid.autoplay = true;
    hidden.appendChild(bgVid);

    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    // Enable high-quality image smoothing so the background image
    // doesn't look pixelated when scaled to fit the 1280x720 canvas.
    // This is THE single biggest quality lever for the composite.
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = "high"; } catch {}
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H); // prime so captureStream emits immediately

    // 60fps composite for perceptibly-smoother motion. Viewers' WebRTC
    // encoder will still downsample to whatever the network can carry,
    // but feeding 60fps in means 30fps out looks crisper (not dropped).
    const produced = canvas.captureStream(60);
    const producedTrack = produced.getVideoTracks()[0];
    // "motion" tells the encoder to prioritise temporal detail (movement)
    // over spatial sharpness — right call for a live camera composite.
    try { if (producedTrack) producedTrack.contentHint = "motion"; } catch {}

    // Publish the produced track up to the parent right away.
    try { onTrackRef.current?.(producedTrack); } catch {}

    // Background source reload — watch for mode/url changes by key.
    // We register onload/onerror each time we (re)assign the src so
    // the UI can surface "loaded" vs "error" and we get a breadcrumb
    // trail in the console for broken URLs.
    let bgKey = "";
    const isDataUrl = (s) => typeof s === "string" && s.startsWith("data:");
    const ensureBg = () => {
      const s = settingsRef.current;
      const src = s.bgDataUrl || s.bgUrl || "";
      const key = `${s.bgMode || "none"}|${src}`;
      if (key === bgKey) return;
      bgKey = key;
      if (s.bgMode === "image" && src) {
        try {
          bgImg.onload = () => {
            setBgStatus("loaded");
            if (window.__TAPIN_GS_DEBUG) console.log("[GS] image loaded", { w: bgImg.naturalWidth, h: bgImg.naturalHeight });
          };
          bgImg.onerror = (ev) => {
            setBgStatus("error");
            console.warn("[GS] image load failed", { src: src.slice(0, 80), ev });
          };
          // Only force CORS for cross-origin URLs; data-URLs are
          // same-origin and some WebKit builds reject them when
          // crossOrigin is anonymous.
          bgImg.crossOrigin = isDataUrl(src) ? null : "anonymous";
          setBgStatus("loading");
          bgImg.src = src;
        } catch (e) {
          setBgStatus("error");
          console.warn("[GS] image src assignment threw", e);
        }
      } else if (s.bgMode === "video" && src) {
        try {
          bgVid.onloadeddata = () => {
            setBgStatus("loaded");
            if (window.__TAPIN_GS_DEBUG) console.log("[GS] video ready", { w: bgVid.videoWidth, h: bgVid.videoHeight });
          };
          bgVid.onerror = () => {
            setBgStatus("error");
            console.warn("[GS] video load failed", { src: src.slice(0, 80) });
          };
          bgVid.crossOrigin = isDataUrl(src) ? null : "anonymous";
          setBgStatus("loading");
          bgVid.src = src;
          bgVid.play().catch(() => {});
        } catch (e) {
          setBgStatus("error");
        }
      } else if (s.bgMode === "blur") {
        setBgStatus("loaded"); // camera itself is the blur source — always "ready"
      } else {
        setBgStatus("none");
      }
    };

    // Offscreen canvas for chroma key — allocated once, reused per frame.
    const off = document.createElement("canvas");
    off.width = W; off.height = H;
    const octx = off.getContext("2d", { willReadFrequently: true });

    // Raw-camera mirror canvas — we copy the un-keyed camera frame here
    // once per render so the eyedropper (in the UI) can sample the
    // user's REAL green-wall color directly via getImageData(). Without
    // this, the eyedropper would sample the already-keyed preview,
    // where the green pixels are already transparent and useless.
    const rawCanvas = document.createElement("canvas");
    rawCanvas.width = W; rawCanvas.height = H;
    const rawCtx = rawCanvas.getContext("2d", { willReadFrequently: true });
    rawCameraCanvasRef.current = rawCanvas;

    // --- MediaPipe Selfie Segmentation (Person Cutout mode) --------------
    // Lazy-loaded from the public CDN the first time the user picks
    // `removalMode: "person"`. We keep a per-pipeline singleton so the
    // model is downloaded once and then reused across frames.
    //
    // ZOOM-STYLE COMPOSITE: instead of thresholding the mask in a JS
    // per-pixel loop (which produces hard pixelated edges), we keep
    // the raw soft alpha mask from MediaPipe, store it on an offscreen
    // canvas, apply a Gaussian blur via `ctx.filter`, and use
    // `globalCompositeOperation = "destination-in"` to apply it as
    // alpha to a copy of the camera frame. The result is silky smooth
    // edges — identical to Zoom / Google Meet / Teams.
    let segmenter = null;
    let segBusy = false;
    // Persistent offscreen "mask canvas" — MediaPipe writes the soft
    // segmentation result here every frame. The render loop reads from
    // this canvas, NOT from getImageData/per-pixel JS, which is the
    // entire reason this approach is smooth.
    const segMaskCanvas = document.createElement("canvas");
    segMaskCanvas.width = W; segMaskCanvas.height = H;
    const segMaskCtx = segMaskCanvas.getContext("2d");
    // Previous-frame mask canvas for temporal smoothing — we draw the
    // previous mask at 35% opacity on top of the new one which dampens
    // edge wobble between frames without a JS pixel loop.
    const segMaskPrev = document.createElement("canvas");
    segMaskPrev.width = W; segMaskPrev.height = H;
    const segMaskPrevCtx = segMaskPrev.getContext("2d");
    let haveMask = false;
    // Subject canvas — the camera frame masked by the blurred segmentation
    // mask. This is what gets composited on top of the background.
    const subjectCanvas = document.createElement("canvas");
    subjectCanvas.width = W; subjectCanvas.height = H;
    const subjectCtx = subjectCanvas.getContext("2d");
    const ensureSegmenter = async () => {
      if (segmenter) return segmenter;
      try {
        if (!window.SelfieSegmentation) {
          await new Promise((res, rej) => {
            const s = document.createElement("script");
            s.src = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/selfie_segmentation.js";
            s.async = true;
            s.onload = res;
            s.onerror = () => rej(new Error("MediaPipe script failed to load"));
            document.head.appendChild(s);
          });
        }
        const SS = window.SelfieSegmentation;
        if (!SS) throw new Error("SelfieSegmentation global missing");
        segmenter = new SS({
          locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${f}`,
        });
        // (E) High-quality "general" model — modelSelection: 0 — runs
        // at ~256x256 internally, ~50% slower than landscape but with
        // a noticeably sharper silhouette. We pay the perf cost in
        // exchange for the cleanest possible cutout, per the user's
        // explicit "best quality" choice.
        // modelSelection: 1 is "landscape" (256×144) — faster, but the
        // silhouette gets ragged at the top of the head. 0 is "general"
        // (256×256) — what Zoom / Google Meet use for room cameras.
        // We pick 0 for the best edge quality.
        segmenter.setOptions({ modelSelection: 0, selfieMode: false });
        segmenter.onResults((results) => {
          if (!results?.segmentationMask) return;
          // 1) Save the *previous* mask before overwriting — used for
          //    temporal smoothing on the next compositing pass.
          segMaskPrevCtx.clearRect(0, 0, W, H);
          if (haveMask) segMaskPrevCtx.drawImage(segMaskCanvas, 0, 0);
          // 2) Draw the new soft mask at full size. MediaPipe returns
          //    a low-res internal mask — drawImage to W×H with
          //    imageSmoothing enabled produces a smooth upscale.
          segMaskCtx.clearRect(0, 0, W, H);
          segMaskCtx.imageSmoothingEnabled = true;
          try { segMaskCtx.imageSmoothingQuality = "high"; } catch {}
          segMaskCtx.drawImage(results.segmentationMask, 0, 0, W, H);
          haveMask = true;
          segBusy = false;
        });
        return segmenter;
      } catch (err) {
        console.warn("[GS] Person Cutout model load failed:", err);
        segmenter = null;
        return null;
      }
    };

    let rafId = 0;
    let frameCounter = 0;
    const tick = () => {
      const s = settingsRef.current;
      ensureBg();

      if (vid.readyState >= 2) {
        // 1) Background
        if (s.bgMode === "image" && bgImg.complete && bgImg.naturalWidth) {
          ctx.drawImage(bgImg, 0, 0, W, H);
        } else if (s.bgMode === "video" && bgVid.readyState >= 2) {
          // cover-fit
          const r = bgVid.videoWidth / bgVid.videoHeight;
          const tr = W / H;
          let dw = W, dh = H, dx = 0, dy = 0;
          if (r > tr) { dw = H * r; dx = (W - dw) / 2; }
          else { dh = W / r; dy = (H - dh) / 2; }
          ctx.drawImage(bgVid, dx, dy, dw, dh);
        } else if (s.bgMode === "blur") {
          ctx.filter = "blur(22px) brightness(0.7)";
          ctx.drawImage(vid, -10, -10, W + 20, H + 20);
          ctx.filter = "none";
        } else {
          ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
        }

        // 2) Remove background using the active removal engine.
        octx.drawImage(vid, 0, 0, W, H);
        // Always mirror the raw camera so the eyedropper works even
        // while person-cutout mode is active (user might toggle back).
        rawCtx.drawImage(vid, 0, 0, W, H);
        let keyedCount = 0;

        // Engine A — Person Cutout (MediaPipe). Ideal when there's no
        // green wall. Zoom-style smooth composite: blur the mask, then
        // use globalCompositeOperation = "destination-in" to apply it
        // as alpha to a copy of the camera. NO per-pixel JS loop —
        // every step is a hardware-accelerated canvas op.
        if (s.removalMode === "person") {
          ensureSegmenter().then((seg) => {
            if (seg && !segBusy) {
              segBusy = true;
              try { seg.send({ image: vid }); }
              catch { segBusy = false; }
            }
          });
          if (haveMask) {
            // a) Draw the camera onto the subject canvas.
            subjectCtx.globalCompositeOperation = "source-over";
            subjectCtx.filter = "none";
            subjectCtx.clearRect(0, 0, W, H);
            subjectCtx.drawImage(vid, 0, 0, W, H);
            // b) Apply the segmentation mask as alpha using the
            //    *blurred* mask via destination-in. The CSS filter
            //    `blur()` feathers the mask edges → soft, smooth alpha
            //    transition. The same `smoothness` slider that already
            //    exists in the UI drives this (0..0.4 mapped to 4..18 px
            //    feather). 0.15 → ~9px feather is the Zoom default look.
            const sm = Math.max(0, Math.min(0.4, s.smoothness ?? 0.15));
            const featherPx = Math.round((sm / 0.4) * 14) + 4; // 4..18 px
            subjectCtx.globalCompositeOperation = "destination-in";
            subjectCtx.filter = `blur(${featherPx}px)`;
            subjectCtx.drawImage(segMaskCanvas, 0, 0, W, H);
            // Temporal smoothing of the mask happens by overlaying
            // the previous mask on the *current* mask before this step
            // would normally run. We removed the post-subject pass
            // because it was multiplying the subject's alpha by 0.30
            // every frame, fading the host into nothing within ~1 s.
            // Edge wobble is already controlled by the Gaussian blur
            // above and MediaPipe's own temporal model.
            subjectCtx.filter = "none";
            subjectCtx.globalCompositeOperation = "source-over";
            // d) Composite subject over the already-drawn background.
            ctx.drawImage(subjectCanvas, 0, 0);
            // No useful "keyed count" in person mode — leave 0.
          }
          // Skip the per-pixel ImageData path entirely.
        } else {
          // Engine B — Chroma Key. The classic path. Requires a
          // physical green/blue wall and a chromaColor that matches
          // the captured color (use the eyedropper to sample).
          const frame = octx.getImageData(0, 0, W, H);
          const data = frame.data;
          const { r: kr, g: kg, b: kb } = hexToRgb(s.chromaColor);
          const sim = (s.similarity ?? 0.4) * 255;
          const smooth = Math.max(1, sim * ((s.smoothness ?? 0.1) * 2));
          for (let i = 0; i < data.length; i += 4) {
            const dr = data[i] - kr;
            const dg = data[i + 1] - kg;
            const db = data[i + 2] - kb;
            const dist = Math.sqrt(dr * dr + dg * dg + db * db);
            if (dist < sim) { data[i + 3] = 0; keyedCount++; }
            else if (dist < sim + smooth) {
              data[i + 3] = Math.round(((dist - sim) / smooth) * 255);
            } else {
              // Spill suppression on opaque subject pixels.
              const r = data[i], g = data[i + 1], b = data[i + 2];
              const rb = Math.max(r, b);
              if (g > rb + 8) data[i + 1] = rb + 4;
            }
          }
          octx.putImageData(frame, 0, 0);
          ctx.drawImage(off, 0, 0);
        }

        // Update keyed-pixel coverage % roughly every 15 frames so
        // the UI doesn't thrash and we don't pay for a React render
        // every single frame. This lets the creator see "is the key
        // actually removing pixels?" — a 0% reading plus a green wall
        // means their chromaColor needs tuning via eyedropper.
        // (Coverage is meaningful only in chroma-key mode — person
        // cutout always reports 0 since it doesn't use a JS pixel pass.)
        if ((++frameCounter & 15) === 0) {
          const totalPixels = W * H;
          const pct = Math.round((keyedCount / totalPixels) * 100);
          setKeyCoverage(pct);
        }

        // Mirror the finished composite into the in-panel preview
        // canvas so the creator gets immediate, unambiguous visual
        // proof of what the outbound stream looks like — camera +
        // background keyed together. If the preview canvas shows the
        // background but the main broadcaster preview doesn't, the
        // bug is in the outbound track-swap, not the compositor.
        const pv = previewCanvasRef.current;
        if (pv) {
          try {
            const pctx = pv.getContext("2d");
            if (pctx) pctx.drawImage(canvas, 0, 0, pv.width, pv.height);
          } catch { /* canvas gone */ }
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      try { cancelAnimationFrame(rafId); } catch {}
      try { produced.getTracks().forEach((t) => t.stop()); } catch {}
      try { vid.srcObject = null; } catch {}
      try { bgImg.onload = null; bgImg.onerror = null; } catch {}
      try { bgVid.onloadeddata = null; bgVid.onerror = null; } catch {}
      try { segmenter?.close?.(); } catch {}
      segmenter = null;
      haveMask = false;
      try { hidden.remove(); } catch {}
      try { onTrackRef.current?.(null); } catch {}
      setBgStatus("idle");
    };
    // Rebuild when enable flips OR the source camera track changes.
  }, [settings.enabled, cameraStream]);

  // --- UI helpers ---------------------------------------------------
  const patch = (next) => setSettings((s) => ({ ...s, ...next }));
  const applyLibraryItem = (it) => {
    patch({
      enabled: true,
      bgMode: it.kind === "video" ? "video" : "image",
      bgUrl: it.url || "",
      bgDataUrl: it.data_url || "",
    });
    toast.success(`Applied: ${it.title}`, { id: "gs-apply", duration: 1800 });
  };

  const generateAi = async (prompt) => {
    if (!prompt) return;
    setGenLoading(true);
    try {
      const { data } = await api.post("/greenscreen/backgrounds/generate", { prompt });
      if (data?.item) {
        setLibrary((prev) => [data.item, ...(prev || [])]);
        applyLibraryItem(data.item);
      }
    } catch (e) {
      toast.error("AI generation failed — try a simpler prompt");
    } finally { setGenLoading(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isVid = f.type.startsWith("video/");
    if (!f.type.startsWith("image/") && !isVid) {
      toast.error("Pick an image or a short video");
      return;
    }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    patch({
      enabled: true,
      bgMode: isVid ? "video" : "image",
      bgDataUrl: dataUrl,
      bgUrl: "",
    });
    toast.success("Background applied");
  };

  // Eyedropper: user clicks anywhere on the live preview; we sample
  // the un-keyed camera pixel at that spot (from rawCanvas, which
  // mirrors the camera without keying) and set it as the chroma key
  // color. This is THE fix for real-world webcams where the physical
  // green wall never photographs as pure RGB(0,255,0).
  const handlePreviewClick = (e) => {
    if (!eyedropperRef.current) return;
    const pv = previewCanvasRef.current;
    const raw = rawCameraCanvasRef.current;
    if (!pv || !raw) return;
    const rect = pv.getBoundingClientRect();
    // Map click in the visible preview canvas to coords in the raw
    // full-res camera canvas.
    const xPct = (e.clientX - rect.left) / rect.width;
    const yPct = (e.clientY - rect.top) / rect.height;
    const sx = Math.max(0, Math.min(raw.width - 1, Math.round(xPct * raw.width)));
    const sy = Math.max(0, Math.min(raw.height - 1, Math.round(yPct * raw.height)));
    try {
      const px = raw.getContext("2d").getImageData(sx, sy, 1, 1).data;
      const hex = "#" + [px[0], px[1], px[2]].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
      patch({ chromaColor: hex, enabled: true });
      setEyedropper(false);
      toast.success(`Key color set to ${hex}`, { id: "gs-eyedrop", duration: 2500 });
    } catch (err) {
      toast.error("Could not sample pixel — try again");
      setEyedropper(false);
    }
  };

  // Collapsed chip (no clicks in the broadcaster preview stack).
  if (collapsed) {
    return (
      <button
        type="button"
        data-testid={`${testidPrefix}-chip`}
        onClick={() => setCollapsed(false)}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition border ${
          settings.enabled
            ? "bg-tv-accent text-black border-tv-accent"
            : "bg-black/60 border-white/15 text-white/80 hover:text-white"
        }`}
        title="Open green-screen studio"
      >
        <Scissors className="w-3 h-3" />
        {settings.enabled ? "GS · ON" : "Green Screen"}
      </button>
    );
  }

  const filteredLib = library || [];

  // On mobile the GS chip's parent is `absolute` inside an
  // `overflow-hidden aspect-video` broadcaster preview. If we render
  // the expanded panel inline, it gets CLIPPED by the preview's rect
  // AND worse, any clicks that land outside that rect get swallowed
  // by the stacking order. Portalling the panel to document.body
  // escapes both: it floats over the whole viewport at z-[200], can
  // be sized/positioned freely, and pointer events work everywhere.
  const panelContent = (
    <div
      data-testid={`${testidPrefix}-panel`}
      className="fixed inset-x-0 bottom-0 sm:bottom-auto sm:top-20 sm:right-4 sm:inset-x-auto sm:w-[340px] w-full max-h-[85vh] z-[200] bg-[#0B0F16]/98 border border-tv-accent/35 sm:rounded-2xl rounded-t-2xl backdrop-blur-sm overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10 bg-black/40">
        <div className="inline-flex items-center gap-2 text-white">
          <Scissors className="w-4 h-4 text-tv-accent" />
          <span className="font-display font-black text-xs uppercase tracking-wider">Green Screen</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`${testidPrefix}-enable`}
              checked={!!settings.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              className="peer sr-only"
            />
            <span className="w-9 h-5 bg-white/15 rounded-full relative transition peer-checked:bg-tv-accent">
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition ${settings.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
            </span>
          </label>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center"
            aria-label="Collapse"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-3 flex-1 overflow-y-auto">
        {/* Live mini-preview — proves the compositor is producing
            valid frames (bg + keyed camera). Only shown when GS is
            enabled and the camera is live. */}
        {settings.enabled && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-white/50">
              <span className="font-bold">Live composite preview</span>
              <span
                data-testid={`${testidPrefix}-bg-status`}
                className={`font-black ${
                  bgStatus === "loaded" ? "text-emerald-400"
                  : bgStatus === "loading" ? "text-amber-400"
                  : bgStatus === "error" ? "text-rose-400"
                  : bgStatus === "none" ? "text-white/40"
                  : "text-white/50"
                }`}
              >
                BG: {bgStatus}
              </span>
            </div>
            <div className="relative">
              <canvas
                ref={previewCanvasRef}
                width={320}
                height={180}
                onClick={handlePreviewClick}
                className={`w-full rounded border bg-black ${
                  eyedropper ? "border-tv-accent cursor-crosshair ring-2 ring-tv-accent animate-pulse" : "border-white/15"
                }`}
                data-testid={`${testidPrefix}-live-preview`}
              />
              {eyedropper && (
                <div className="absolute inset-x-0 bottom-0 bg-tv-accent/90 text-black text-[10px] font-black text-center py-1 uppercase tracking-wider">
                  Click your green wall to sample
                </div>
              )}
            </div>
            {/* Eyedropper + coverage readout — creator-facing diagnostics.
                Only meaningful in Chroma Key mode; Person Cutout uses
                ML segmentation so there's no color to sample. */}
            {settings.removalMode === "chroma" ? (
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  data-testid={`${testidPrefix}-eyedropper`}
                  onClick={() => setEyedropper((v) => !v)}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-black uppercase tracking-wider border transition ${
                    eyedropper
                      ? "bg-tv-accent text-black border-tv-accent"
                      : "bg-white/5 border-white/15 text-white hover:bg-white/10"
                  }`}
                  title="Click then tap your physical green wall in the preview to set the exact key color — THE fix for real webcams"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/></svg>
                  {eyedropper ? "Click the green wall…" : "Pick key color from camera"}
                </button>
                <span
                  data-testid={`${testidPrefix}-coverage`}
                  className={`text-[10px] font-black tabular-nums px-2 py-1.5 rounded ${
                    keyCoverage == null ? "bg-white/5 text-white/40"
                    : keyCoverage < 5 ? "bg-rose-500/20 text-rose-300"
                    : keyCoverage < 25 ? "bg-amber-500/20 text-amber-300"
                    : "bg-emerald-500/20 text-emerald-300"
                  }`}
                  title="Fraction of camera pixels being keyed out. 0% means the key color doesn't match your green wall — use the eyedropper."
                >
                  {keyCoverage == null ? "— " : `${keyCoverage}%`}
                </span>
              </div>
            ) : (
              <div className="text-[10px] text-emerald-300/80 italic leading-snug pt-1">
                Person Cutout active — removes the background automatically using on-device AI. No green wall needed.
              </div>
            )}
            {/* (F) Shutter speed slider — only when the camera driver
                 exposes the manual-exposure capability. Faster shutter
                 = sharper edges = cleaner cutout. */}
            {shutterCaps && (
              <div className="space-y-1 pt-1">
                <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-white/50 font-black">
                  <span>Shutter speed</span>
                  <button
                    type="button"
                    onClick={resetShutter}
                    className="text-white/40 hover:text-white normal-case text-[9px] tracking-normal font-bold"
                  >
                    Auto
                  </button>
                </div>
                <input
                  type="range"
                  min={shutterCaps.min}
                  max={Math.min(shutterCaps.max, 5000)}
                  step={shutterCaps.step}
                  value={shutterValue ?? shutterCaps.min}
                  onChange={(e) => applyShutter(parseInt(e.target.value, 10))}
                  data-testid={`${testidPrefix}-shutter`}
                  className="w-full accent-tv-accent"
                  aria-label="Camera shutter speed"
                />
                <div className="flex justify-between text-[9px] text-white/40 tabular-nums">
                  <span>Fast (sharp edges)</span>
                  <span>{shutterValue ? `${shutterValue}` : "—"}</span>
                  <span>Slow (brighter)</span>
                </div>
              </div>
            )}
            {settings.removalMode === "chroma" && keyCoverage != null && keyCoverage < 5 && (
              <div className="text-[10px] text-amber-300 italic leading-snug">
                Only {keyCoverage}% keyed — your green doesn't match the key color. Tap <strong>Pick key color from camera</strong>, then click your green wall.
              </div>
            )}
            {bgStatus === "error" && (
              <div className="text-[10px] text-rose-300 italic">
                Background failed to load. Try a different item, or re-upload.
              </div>
            )}
          </div>
        )}

        {/* Removal engine picker — Chroma Key vs Person Cutout. The
            mode toggle is the single most important setting: chroma
            needs a physical green/blue wall; person cutout works
            anywhere via ML segmentation. We render this BEFORE the
            background mode row so the user's mental model is "pick
            how to remove → pick what to replace with". */}
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wider text-white/40 font-black">Removal engine</div>
          <div className="grid grid-cols-2 gap-1.5 text-[10px] uppercase tracking-wider font-black">
            <button
              type="button"
              data-testid={`${testidPrefix}-mode-chroma`}
              onClick={() => patch({ removalMode: "chroma" })}
              aria-pressed={settings.removalMode === "chroma"}
              title="Uses a chroma key — needs a physical green or blue wall"
              className={`px-2 py-2 rounded border transition text-left leading-tight ${
                settings.removalMode === "chroma"
                  ? "bg-tv-accent text-black border-tv-accent"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
            >
              Chroma Key
              <div className="text-[8px] normal-case opacity-80 font-bold tracking-normal mt-0.5">Green / blue wall</div>
            </button>
            <button
              type="button"
              data-testid={`${testidPrefix}-mode-person`}
              onClick={() => patch({ removalMode: "person" })}
              aria-pressed={settings.removalMode === "person"}
              title="Person cutout — ML segmentation, works anywhere (no green wall needed)"
              className={`px-2 py-2 rounded border transition text-left leading-tight ${
                settings.removalMode === "person"
                  ? "bg-tv-accent text-black border-tv-accent"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
            >
              Person Cutout
              <div className="text-[8px] normal-case opacity-80 font-bold tracking-normal mt-0.5">No wall · uses AI</div>
            </button>
          </div>
        </div>

        {/* Mode row */}
        <div className="grid grid-cols-4 gap-1.5 text-[10px] uppercase tracking-wider font-black">
          {[
            { k: "none", label: "None" },
            { k: "blur", label: "Blur" },
            { k: "image", label: "Image" },
            { k: "video", label: "Video" },
          ].map((m) => (
            <button
              key={m.k}
              type="button"
              data-testid={`${testidPrefix}-mode-${m.k}`}
              onClick={() => patch({ bgMode: m.k })}
              aria-pressed={settings.bgMode === m.k}
              className={`px-2 py-1.5 rounded border transition ${
                settings.bgMode === m.k
                  ? "bg-tv-accent text-black border-tv-accent"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Library grid — instant-apply on click */}
        {(settings.bgMode === "image" || settings.bgMode === "video" || settings.bgMode === "none") && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-white/50">
              <span className="inline-flex items-center gap-1 font-bold">
                <ImageIcon className="w-3 h-3" /> Library
              </span>
              {library === null && <Loader2 className="w-3 h-3 animate-spin text-white/40" />}
            </div>
            {filteredLib.length === 0 && library !== null && (
              <div className="text-[10px] text-white/40 italic">Library is empty — generate one below or upload your own.</div>
            )}
            {filteredLib.length > 0 && (
              <div className="grid grid-cols-3 gap-1.5">
                {filteredLib.slice(0, 18).map((it) => {
                  const active = (settings.bgUrl && it.url === settings.bgUrl) ||
                                 (settings.bgDataUrl && it.data_url === settings.bgDataUrl);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-testid={`${testidPrefix}-lib-${it.id}`}
                      onClick={() => applyLibraryItem(it)}
                      title={it.title}
                      className={`relative aspect-video rounded overflow-hidden border ${
                        active ? "border-tv-accent ring-1 ring-tv-accent" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      {it.kind === "video" ? (
                        <div className="w-full h-full bg-gradient-to-br from-violet-900/50 to-indigo-900/50 flex items-center justify-center">
                          <span className="text-[8px] font-black text-white/80">VID</span>
                        </div>
                      ) : (
                        <img
                          src={it.data_url || it.url}
                          alt={it.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[7px] uppercase tracking-wider font-bold px-1 py-0.5 truncate">
                        {it.title}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Upload */}
        <div>
          <label
            data-testid={`${testidPrefix}-upload-label`}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1.5 text-[10px] font-black uppercase tracking-wider text-white cursor-pointer"
          >
            <Upload className="w-3 h-3 text-tv-accent" /> Upload
            <input type="file" accept="image/*,video/*" className="hidden" onChange={onFile} data-testid={`${testidPrefix}-upload`} />
          </label>
        </div>

        {/* AI-gen — quick prompt */}
        <AiGenRow disabled={genLoading} onGenerate={generateAi} testidPrefix={testidPrefix} />

        {/* Chroma sliders — collapsed by default */}
        <div>
          <button
            type="button"
            onClick={() => setShowSliders((v) => !v)}
            className="w-full inline-flex items-center justify-between text-[10px] uppercase tracking-wider text-white/60 hover:text-white font-bold"
          >
            <span>Chroma fine-tune</span>
            <span>{showSliders ? "–" : "+"}</span>
          </button>
          {showSliders && (
            <div className="mt-2 space-y-2 bg-black/30 border border-white/10 rounded p-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/60 w-16">Key color</span>
                <input
                  type="color"
                  data-testid={`${testidPrefix}-color`}
                  value={settings.chromaColor}
                  onChange={(e) => patch({ chromaColor: e.target.value })}
                  className="h-6 w-10 rounded cursor-pointer bg-transparent"
                />
                <code className="text-[10px] text-white/70">{settings.chromaColor}</code>
              </div>
              <Slider
                label="Similarity"
                value={settings.similarity}
                min={0.1}
                max={0.95}
                step={0.02}
                onChange={(v) => patch({ similarity: v })}
                testid={`${testidPrefix}-similarity`}
              />
              <Slider
                label="Smoothness"
                value={settings.smoothness}
                min={0}
                max={0.5}
                step={0.01}
                onChange={(v) => patch({ smoothness: v })}
                testid={`${testidPrefix}-smoothness`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Portal to body so the panel escapes any parent `overflow-hidden`
  // on mobile, AND add a backdrop that closes on tap so users can't
  // accidentally leave the panel "stuck" over the preview.
  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[199] bg-black/40 sm:bg-transparent"
        onClick={() => setCollapsed(true)}
        data-testid={`${testidPrefix}-backdrop`}
      />
      {panelContent}
    </>,
    document.body,
  );
}

function AiGenRow({ disabled, onGenerate, testidPrefix }) {
  const [prompt, setPrompt] = useState("");
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="AI: describe a background…"
        className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] text-white outline-none focus:border-tv-accent"
        data-testid={`${testidPrefix}-ai-input`}
      />
      <button
        type="button"
        data-testid={`${testidPrefix}-ai-gen`}
        onClick={() => { const p = prompt.trim(); if (p) onGenerate(p); }}
        disabled={disabled || !prompt.trim()}
        className="inline-flex items-center gap-1 bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black text-[10px] font-black uppercase tracking-wider rounded px-2 py-1.5"
      >
        {disabled ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
        Gen
      </button>
    </div>
  );
}

function Slider({ label, value, min, max, step, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
        <span>{label}</span>
        <span className="tabular-nums">{Number(value).toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-tv-accent"
        data-testid={testid}
      />
    </div>
  );
}
